import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SimpleDropDown from '@panel/commons/lib/DropDown/SimpleDropDown';
import { useDispatch, useSelector } from 'react-redux';
import uuid from 'uuid-random';
import styled from 'styled-components';
import { select } from '@panel/commons/core/themev2';
import { getColor } from '@panel/commons/core/themev3';
import PropTypes from 'prop-types';
import Loader from '@panel/commons/lib/SimpleLoader';
import Alert from '@panel/commons/lib/Alert/Alert';
import Atencion from '../../../assets/img/atencion.svg';
import { ResponsibleUpdateSelectors, ResponsibleUpdateOperations } from '../../../ducks/responsibleUpdate';

const UpdateResponsibleModal = ({ onClose, postings, isIntegrator = false }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    collaboratorsInfo,
    hasCollaboratorsInfo,
    errorCollaborators,
    loadingCollaborators,
    updatedUserResponsible
  } = useSelector(ResponsibleUpdateSelectors.getResponsibleUpdateState);
  const hasUniqueUser = collaboratorsInfo && collaboratorsInfo.length === 1;

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [userResponsible, setUserResponsible] = useState(null);
  const [sortedCollaborators, setSortedCollaborators] = useState([]);

  /**
   * Retorna el texto que se va a mostrar en las opciones del DropDown.
   * @param {Object} collaborator
   * @returns {String} se devuelve una traducción o los datos del usuario.
   */
  const getTextDropDown = collaborator => {
    if (!collaborator) return t('panel.updateResponsible.modal.dropdown.placeholder');
    if (!collaborator.email) return `${collaborator.name}`;
    if (!collaborator.lastName && !collaborator.phone) return `${collaborator.name} (${collaborator.email})`;
    if (!collaborator.lastName) return `${collaborator.name} (${collaborator.email} - ${collaborator.phone})`;
    if (collaborator.phone && collaborator.lastName)
      return `${collaborator.name} ${collaborator.lastName} (${collaborator.email} - ${collaborator.phone})`;
    return `${collaborator.name} ${collaborator.lastName} (${collaborator.email})`;
  };

  /**
   * La función ordena el listado de usuarios en primer lugar por Responsable y luego por tipo de usuario (Admin > Superusuario)
   * @param {Array} usersArray
   * @returns se retorna el array de usuarios ordenado
   */
  const sortCollaboratorsList = collaboratorsArray => {
    if (!collaboratorsArray) return [];
    collaboratorsArray.sort((a, b) => {
      if (a.isResponsible > b.isResponsible) return -1;
      if (a.isResponsible < b.isResponsible) return 1;
      if (a.permission > b.permission) return -1;
      if (b.permission > a.permission) return 1;
      return a.userTypeId - b.userTypeId;
    });
    return collaboratorsArray;
  };

  useEffect(() => {
    // Se realiza FETCH para armar el dropdown con los usuarios:
    if (!errorCollaborators) dispatch(ResponsibleUpdateOperations.getCollaboratorsInfo(postings && postings[0]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Se ordena el listado de usuarios, mostrando en primer lugar los usuarios con permisos para editar datos:
    setSortedCollaborators(sortCollaboratorsList(collaboratorsInfo));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collaboratorsInfo, updatedUserResponsible, postings]);

  if (loadingCollaborators)
    return (
      <LoaderWrapper>
        <h1>{t('panel.updateResponsible.modal.cta_accept')}</h1>
        <Loader />
      </LoaderWrapper>
    );

  if (hasUniqueUser)
    return (
      <OnlyUserContainer>
        <Img data-panel='updateResponsible-imageAtencion' src={Atencion} />
        <h1>{t('panel.updateResponsible.modal.onlyUser.title')}</h1>
        <p>
          {t('panel.updateResponsible.modal.onlyUser.onlyUser.info')}{' '}
          <a href={`${window.location.origin}/configuracion/adminUsuarios.bum`}>
            {t('panel.updateResponsible.modal.onlyUser.onlyUser.info_link')}
          </a>
        </p>
        <Button
          width='220px'
          className='solidButton'
          data-panel='updateResponsible-cta-aceptar'
          onClick={() => onClose()}
        >
          {t('panel.updateResponsible.modal.onlyUser.close')}
        </Button>
      </OnlyUserContainer>
    );

  return (
    <Container data-panel='updateResponsible'>
      <div className='infoContain'>
        <h3 data-panel='updateResponsible-title'>
          {t(`panel.updateResponsible.modal.title${postings.length > 1 ? '.plural' : ''}`, { value: postings.length })}
        </h3>
      </div>
      <p data-panel='updateResponsible-subTitle'>
        {t(`panel.updateResponsible.modal.info${postings.length > 1 ? '.plural' : ''}`)}
      </p>
      {hasCollaboratorsInfo && (
        <div className='dropdownContainer'>
          <Title>{t('panel.updateResponsible.modal.dropdown.label')}</Title>
          <SimpleDropDown
            dropdownOpen={dropdownOpen}
            setDropdownOpen={setDropdownOpen}
            textHeader={getTextDropDown(userResponsible)}
            tooltip={getTextDropDown(userResponsible)}
            textColor={!userResponsible ? 'midDarkGrayAux' : ''}
          >
            {sortedCollaborators &&
              sortedCollaborators.length > 0 &&
              sortedCollaborators.map(collaborator => (
                <OpenDropdownOptions
                  key={uuid()}
                  data-panel={`dropdown-${collaborator.name}`}
                  title={getTextDropDown(collaborator)}
                  onClick={() => setUserResponsible(collaborator)}
                >
                  <div className='option'>{getTextDropDown(collaborator)}</div>
                </OpenDropdownOptions>
              ))}
          </SimpleDropDown>
        </div>
      )}
      {isIntegrator && (
        <AlertContainer>
          <Alert
            message={t('panel.updateResponsible.isIntegrator.message')}
            dataPabel='updateResponsible'
            minHeight='46px'
            fontSize='14px'
            justifyContent='start'
            withoutBorder
          />
        </AlertContainer>
      )}
      <ButtonContainer>
        <Button
          width='100%'
          className='noBorderButton'
          fontSize='18px'
          fontWeight='500'
          data-panel='updateResponsible-cta-cancel'
          onClick={() => onClose()}
        >
          {t('panel.updateResponsible.modal.cta_cancel')}
        </Button>
        <Button
          width='100%'
          className='solidButton'
          data-panel='updateResponsible-cta-aceptar'
          disabled={!userResponsible}
          onClick={() => {
            if (userResponsible) {
              dispatch(ResponsibleUpdateOperations.updateUserResponsibleBulk(userResponsible.userId, postings));
              setUserResponsible(null);
              onClose();
            }
          }}
        >
          {t('panel.updateResponsible.modal.cta_accept')}
        </Button>
      </ButtonContainer>
    </Container>
  );
};

const Container = styled.div`
  height: 100%;
  padding: 32px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
  font-family: Hind;
  font-style: normal;
  .infoContain {
    display: flex;
    align-items: center;
    justify-content: center;
    h3 {
      max-width: 343px;
      font-size: 20px;
      font-weight: 600;
      line-height: 130%;
      text-align: center;
      white-space: normal;
    }
  }
  > p {
    font-size: 14px;
    font-weight: 400;
    line-height: normal;
    text-align: center;
    white-space: normal;
  }
  .dropdownContainer {
    width: 100%;
    display: block;
    max-height: 350px;
    z-index: 1;
  }
`;

const ButtonContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
`;

const Button = styled.div`
  width: ${({ width }) => width};
  height: 40px;
  border-radius: ${({ borderRadius }) => borderRadius || '8px'};
  font-size: ${({ fontSize }) => fontSize || '16px'};
  font-weight: ${({ fontWeight }) => fontWeight || '600'};
  line-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ disabled }) => (disabled ? 'auto' : 'pointer')};
  &.solidButton {
    background-color: ${({ disabled }) => (!disabled ? getColor('primary') : select('lightGray'))};
    color: ${({ disabled }) => select(!disabled ? 'white' : 'midLightGray')};
    border-color: ${({ disabled }) => disabled && select('lightGray')};
    &:hover {
      background-color: ${({ disabled }) => !disabled && getColor('secondary')};
    }
  }
  &.noBorderButton {
    background-color: ${select('transparent')};
    &:hover {
      background-color: ${select('lightGray')};
    }
  }
`;

const AlertContainer = styled.div`
  width: 100%;
`;

const Title = styled.div`
  width: 100%;
  font-family: Hind;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  text-align: left;
  margin-bottom: 4px;
`;

const OpenDropdownOptions = styled.div`
  font-family: Hind;
  font-size: 14px;
  font-weight: 400;
  line-height: 25.62px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  padding: 8px;
  width: 100%;
  cursor: pointer;
  :hover {
    background-color: ${select('neutralGray')};
  }
  .option {
    width: 430px;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

const LoaderWrapper = styled.div`
  width: 512px;
  height: 466px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  h1 {
    font-family: Hind;
    font-size: 20px;
    font-style: normal;
    font-weight: 600;
    line-height: 130%;
    margin-bottom: 125px;
  }
`;

const OnlyUserContainer = styled.div`
  width: 512px;
  height: 409px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  gap: 32px;
  h1 {
    font-family: Hind;
    font-size: 20px;
    font-style: normal;
    font-weight: 600;
    line-height: 130%;
  }
  p {
    font-family: Hind;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    white-space: normal;
    text-align: center;
    a {
      color: ${select('blueLinks')};
      text-decoration: none;
    }
  }
`;

const Img = styled.div`
  background-image: url(${({ src }) => src});
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  width: 160px;
  height: 108px;
  margin-top: 37px;
`;

UpdateResponsibleModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  postings: PropTypes.arrayOf(PropTypes.number).isRequired
};

export default UpdateResponsibleModal;
